/**
 * POS search coverage memo.
 *
 * Under word-prefix matching, appending a character to a query either extends
 * its last token or starts a new one — both strictly narrow the result set. So
 * results for "colga" are a subset of results for "col", and one *complete*
 * server response for "col" legitimately covers every longer keystroke. That is
 * what lets typing "colgate" cost one request instead of seven.
 *
 * Deliberately in memory rather than IndexedDB: writing on every keystroke would
 * reintroduce the latency this exists to remove. Losing it on reload costs one
 * request.
 *
 * This memo governs catalogue *membership* only (new/renamed/deleted variants).
 * Quantities stay fresh independently via syncBranchStock's TTL and the
 * branch_stocks realtime invalidation, and the local leg re-reads
 * branch_products on every keystroke — so suppressing a network search can
 * never serve stale stock.
 */
import { normalizeTerm } from '@/utils/searchTokens.js';

export const COVERAGE_TTL_MS = 5 * 60 * 1000;

/** Bound the map so a long shift of typing cannot grow it without limit. */
const MAX_ENTRIES = 500;

const coverage = new Map();

/**
 * Results depend on which branches were searched, so origin-only and
 * company-wide coverage must never share an entry.
 */
export const poolKeyFor = (branchIds = []) =>
    [...new Set((branchIds ?? []).map((id) => Number(id)).filter((id) => id > 0))]
        .sort((a, b) => a - b)
        .join(',');

const prune = (now) => {
    for (const [key, entry] of coverage) {
        if (now - entry.at >= COVERAGE_TTL_MS) coverage.delete(key);
    }
    if (coverage.size <= MAX_ENTRIES) return;
    // Still oversized after dropping stale entries: drop oldest first.
    const byAge = [...coverage.entries()].sort((a, b) => a[1].at - b[1].at);
    for (const [key] of byAge.slice(0, coverage.size - MAX_ENTRIES)) {
        coverage.delete(key);
    }
};

/**
 * Is some prefix of `term` already known complete and fresh for this pool?
 * Walks longest to shortest, since the longest match is the most specific.
 */
export const isSearchCovered = (poolKey, term, now = Date.now()) => {
    const normalized = normalizeTerm(term);
    if (!poolKey || !normalized) return false;

    for (let end = normalized.length; end > 0; end -= 1) {
        const entry = coverage.get(`${poolKey}|${normalized.slice(0, end)}`);
        // A stale entry does not rule out a shorter prefix still being fresh.
        if (entry?.complete && now - entry.at < COVERAGE_TTL_MS) return true;
    }
    return false;
};

/**
 * Record the outcome of one server search. Only `term` itself is recorded — a
 * shorter prefix is a superset and cannot be inferred complete from a longer one.
 */
export const recordSearchCoverage = (poolKey, term, { complete } = {}, now = Date.now()) => {
    const normalized = normalizeTerm(term);
    if (!poolKey || !normalized) return;

    coverage.set(`${poolKey}|${normalized}`, { at: now, complete: Boolean(complete) });
    prune(now);
};

export const clearSearchCoverage = () => {
    coverage.clear();
};

/** Test/diagnostic helper. */
export const searchCoverageSize = () => coverage.size;
